import { forwardRef, ReactNode, useCallback, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import * as Sentry from '@sentry/browser';
import { IStyled } from '../../styles/types';
import { ChartMode } from './types';
import { ChartContextProvider } from './ChartContext';
import * as ChartUI from './ChartUI';
import ChartMain from './ChartMain';
import ChartModals from './ChartModals';
import { FullscreenContextProvider } from '../../utils/contexts/FullscreenContext';
import { FullscreenContainer } from '../FullscreenContainer/FullscreenContainer';
import { ChartHeader } from './ChartHeader';
import ChartFallback from './ChartFallback';
import ChartSummaryRichText from './ChartSummaryRichText';
import ChartBuilderBanner from './ChartBuilderBanner';
import Banner from '../Banner';
import { useCurrentUrl } from '../../utils/routing';
import styled from 'styled-components';
import { useAppSelector } from '../../redux/hooks';
import { analysisSelector } from '../../redux/selectors/analyses';
import { ChartLoader } from './ChartLoader';
import { ChartReplaceReportEffect } from './Effects';
import { CentralChartSettingProvider } from './CentralChartSetting/contexts/CentralChartSettingContext';

/**
 * An entry point component for all charts.
 * It should be used everywhere where we render report.
 */

interface IProps extends IStyled {
  analysisId: string;
  mode: ChartMode;
  isDragSelected?: boolean;
  isDragging?: boolean;
  optionsEnabled?: boolean;
  onDelete?: () => void;
  inDashboard?: boolean;
  dragHandle?: ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isEmbedded?: boolean;
  rebuildChart?: () => void;
}

const StyledBanner = styled(Banner)<{
  $compact: boolean;
}>`
  font-size: ${({ $compact }) => ($compact ? '12px' : '14px')};
`;

const Chart = forwardRef<HTMLDivElement, IProps>(
  (
    {
      analysisId,
      mode,
      isDragSelected,
      isDragging,
      optionsEnabled,
      onDelete,
      className,
      inDashboard,
      dragHandle,
      onMouseEnter,
      onMouseLeave,
      isEmbedded = false,
      rebuildChart,
    },
    ref,
  ) => {
    const { isDashboardInUrl } = useCurrentUrl();
    const handleErrorBoundary = useCallback(
      (error: Error) => {
        console.error('Error: ', error);
        Sentry.captureException({ ...error, analysisId });
      },
      [analysisId],
    );

    const analysisMemoizedSelector = useMemo(analysisSelector, []);
    const analysis = useAppSelector((state) =>
      analysisMemoizedSelector(state, analysisId),
    );
    const isSchemaDisabled = analysis?.metadata?.query.schema.disabled ?? true;

    // check is metaData ready, if not, it means the populateAnalysis is still in progress.
    if (!analysis?.metadata) {
      return <ChartLoader mode={mode} />;
    }

    return (
      <ChartContextProvider
        mode={mode}
        chartOptionsEnabled={optionsEnabled}
        onDelete={onDelete}
        dragHandleElement={dragHandle}
        analysis={analysis}
        isEmbedded={isEmbedded}
        rebuildChart={rebuildChart}
      >
        <ErrorBoundary
          fallback={<ChartFallback />}
          onError={handleErrorBoundary}
        >
          <CentralChartSettingProvider>
            {mode === ChartMode.Build && !isSchemaDisabled && (
              <ChartBuilderBanner />
            )}
            <FullscreenContextProvider>
              <FullscreenContainer>
                <ChartReplaceReportEffect />
                <ChartUI.Container
                  ref={ref}
                  className={className}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  $isSelected={isDragSelected}
                  $isDragging={isDragging}
                >
                  <ChartHeader />
                  {isSchemaDisabled && (
                    <StyledBanner
                      variant="disabled"
                      $compact={isDashboardInUrl}
                      icon="disconnected"
                      bannerStyle={isDashboardInUrl ? 'compact' : 'regular'}
                      selfClosing={!isDashboardInUrl}
                    >
                      This report is from a disabled schema. Data cannot be
                      refreshed and is a historical snapshot.
                    </StyledBanner>
                  )}
                  {mode !== ChartMode.Compact && (
                    <ChartSummaryRichText
                      inDashboard={inDashboard}
                      analysisId={analysisId}
                    />
                  )}
                  <ChartUI.ChartMainWrapper $mode={mode}>
                    <ChartMain />
                  </ChartUI.ChartMainWrapper>
                </ChartUI.Container>
              </FullscreenContainer>
            </FullscreenContextProvider>
            <ChartModals analysisId={analysisId} />
          </CentralChartSettingProvider>
        </ErrorBoundary>
      </ChartContextProvider>
    );
  },
);

export default Chart;
